import { useMemo } from "react";

interface Value {
  value: string;
  classname?: string;
}

export type GradientInputState = {
  color: Value;
  position?: Value;
  set: boolean;
};

export interface GradientState {
  from?: GradientInputState;
  via?: GradientInputState;
  to?: GradientInputState;
  direction: string;
}

/**
 * This hook is composed of 4 phases:
 * 1. Extracting meaningful information from the CSS gradient string.
 * 2. Shaping it into a structured object.
 * 3. Relating the structured object to the classes on the selected element
 * 4. Returning all this information as GradientState.
 */

const parseCssGradient = (gradient: string) => {
  // Validation
  if (!gradient) return null;

  if (!gradient.startsWith("linear-gradient(") || !gradient.endsWith(")")) {
    return null;
  }

  // Extract the content inside the parentheses
  const content = gradient.slice(16, -1).trim();

  // Use a regex to split the content while considering CSS color functions like rgb, rgba, hsl
  const regex = /(?<=\)),|(?<!\([^)]*),(?![^(]*\))/g;
  const parts = content.split(regex).map((part) => part.trim());

  if (parts.length < 2) {
    console.error(
      "Gradient must include a direction and at least one color stop",
    );
    return null;
  }

  // Check if the first part is a direction or a color stop
  const isDirection = (part) => {
    const degRegex = /\d+deg/; // matches any number followed by 'deg'
    const sideRegex = /^to (left|right|top|bottom)( (left|right|top|bottom))?$/; // matches 'to left', 'to top right', etc.

    return degRegex.test(part) || sideRegex.test(part);
  };

  if (!isDirection(parts[0])) {
    parts.unshift("to bottom"); // Default direction if no valid direction is specified
  }

  // The first part is the direction, the rest are color stops
  const direction = parts[0];
  const colorStops = parts.slice(1).map((stop) => {
    // Regex to extract the color and the optional position
    const colorStopRegex = /^(.+?)(\s+\d+%|)$/;
    const match = colorStopRegex.exec(stop.trim());

    if (match) {
      return {
        color: match[1].trim(),
        position: match[2].trim() || undefined, // Include position only if it exists
      };
    }

    return { color: stop, position: undefined }; // Fallback if regex doesn't match
  });

  // Return structured gradient data
  return { direction, colorStops };
};

const useGradient = (
  gradient,
  classes,
  hasGradientClass = true,
): GradientState | null => {
  return useMemo(() => {
    if (!hasGradientClass) return null;
    if (!gradient) return null;
    if (!classes) return null;

    const directionClass = classes.find((cls) =>
      cls.startsWith("bg-gradient-"),
    );
    const parsedGradient = parseCssGradient(gradient);

    if (!parsedGradient) return null;

    let gradientState: GradientState = {
      direction: parsedGradient.direction,
    };

    const fromColorClass = classes.find(
      (cls) => cls.startsWith("from-") && !cls.includes("%"),
    );
    const viaColorsClass = classes.find(
      (cls) => cls.startsWith("via-") && !cls.includes("%"),
    );
    const toColorsClass = classes.find(
      (cls) => cls.startsWith("to-") && !cls.includes("%"),
    );
    const fromPositionClass = classes.find(
      (cls) => cls.startsWith("from-") && cls.includes("%"),
    );
    const viaPositionClass = classes.find(
      (cls) => cls.startsWith("via-") && cls.includes("%"),
    );
    const toPositionClass = classes.find(
      (cls) => cls.startsWith("to-") && cls.includes("%"),
    );

    // Knowing the number of color stops is not enough to determine the gradient structure. A gradient can have 2 or 3 color stops, but it can be a result of:
    // 1. A single from-color (which implicitly sets a to-color of the same color with 0 alpha)
    // 2. A from-color and a to-color.
    // 3. A from-color and a via-color.
    // 4. A from-color, a via-color, and a to-color.
    // Note: A gradient will only appear if it has a from-color set.

    if (!fromColorClass) {
      gradientState = {
        ...gradientState,
        from: {
          set: false,
          // @ts-expect-error TS2322
          color: undefined,
        },
        via: {
          set: false,
          // @ts-expect-error TS2322
          color: undefined,
        },
        to: {
          set: false,
          // @ts-expect-error TS2322
          color: undefined,
        },
      };
    }

    if (fromColorClass && !toColorsClass && !viaColorsClass) {
      if (parsedGradient.colorStops.length !== 2) {
        return null;
      }

      gradientState = {
        ...gradientState,
        from: {
          color: {
            value: parsedGradient.colorStops[0].color,
            classname: fromColorClass,
          },
          set: true,
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[0].position,
            classname: fromPositionClass,
          },
        },
        to: {
          color: {
            value: parsedGradient.colorStops[1].color,
          },
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[1].position,
          },
          set: false,
        },
      };
    } else if (fromColorClass && toColorsClass && !viaColorsClass) {
      if (parsedGradient.colorStops.length !== 2) {
        return null;
      }

      gradientState = {
        ...gradientState,
        from: {
          color: {
            value: parsedGradient.colorStops[0].color,
            classname: fromColorClass,
          },
          set: true,
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[0].position,
            classname: fromPositionClass,
          },
        },
        to: {
          color: {
            value: parsedGradient.colorStops[1].color,
            classname: toColorsClass,
          },
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[1].position,
            classname: toPositionClass,
          },
          set: true,
        },
      };
    } else if (fromColorClass && !toColorsClass && viaColorsClass) {
      if (parsedGradient.colorStops.length !== 3) {
        return null;
      }

      gradientState = {
        ...gradientState,
        from: {
          color: {
            value: parsedGradient.colorStops[0].color,
            classname: fromColorClass,
          },
          set: true,
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[0].position,
            classname: fromPositionClass,
          },
        },
        via: {
          color: {
            value: parsedGradient.colorStops[1].color,
            classname: viaColorsClass,
          },
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[1].position,
            classname: viaPositionClass,
          },
          set: true,
        },
        to: {
          color: {
            value: parsedGradient.colorStops[2].color,
          },
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[2].position,
          },
          set: false,
        },
      };
    } else if (fromColorClass && toColorsClass && viaColorsClass) {
      if (parsedGradient.colorStops.length !== 3) {
        return null;
      }

      gradientState = {
        ...gradientState,
        from: {
          color: {
            value: parsedGradient.colorStops[0].color,
            classname: fromColorClass,
          },
          set: true,
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[0].position,
            classname: fromPositionClass,
          },
        },
        via: {
          color: {
            value: parsedGradient.colorStops[1].color,
            classname: viaColorsClass,
          },
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[1].position,
            classname: viaPositionClass,
          },
          set: true,
        },
        to: {
          color: {
            value: parsedGradient.colorStops[2].color,
            classname: toColorsClass,
          },
          position: {
            // @ts-expect-error TS2322
            value: parsedGradient.colorStops[2].position,
            classname: toPositionClass,
          },
          set: true,
        },
      };
    }

    return gradientState;
  }, [
    gradient,
    Array.isArray(classes) ? classes.join(",") : "",
    hasGradientClass,
  ]);
};

export default useGradient;
